import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { adminApi } from '../lib/api';
import { Building2, Eye, Search, CreditCard, Power, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export default function AdminBusinesses() {
    const { updateToken } = useAuth();
    const navigate = useNavigate();
    const [businesses, setBusinesses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    useEffect(() => {
        adminApi.getBusinesses().then(({ data }) => setBusinesses(data)).catch(() => toast.error('Failed to load businesses')).finally(() => setLoading(false));
    }, []);

    const handleViewBusiness = async (businessId) => {
        try {
            const { data } = await adminApi.switchContext(businessId);
            updateToken(data.access_token, data.business);
            toast.success(data.message);
            navigate('/dashboard');
        } catch {
            toast.error('Failed to switch context');
        }
    };

    const handleToggleStatus = async (businessId, currentStatus) => {
        const newStatus = currentStatus === 'active' ? 'cancelled' : 'active';
        try {
            await adminApi.updateBusinessStatus(businessId, true, newStatus);
            toast.success(`Subscription ${newStatus === 'active' ? 'activated' : 'cancelled'}`);
            adminApi.getBusinesses().then(({ data }) => setBusinesses(data));
        } catch { toast.error('Failed to update'); }
    };

    const [deletingId, setDeletingId] = useState(null);

    const handleDelete = async (businessId, businessName) => {
        if (deletingId === businessId) {
            // Second click = confirmed
            try {
                const { data } = await adminApi.deleteBusiness(businessId);
                toast.success(data.message);
                setBusinesses(prev => prev.filter(b => b.id !== businessId));
            } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
            setDeletingId(null);
        } else {
            // First click = arm confirmation
            setDeletingId(businessId);
            toast.warning(`Click Delete again to permanently remove "${businessName}"`, { duration: 4000 });
            setTimeout(() => setDeletingId(null), 4000);
        }
    };

    const filtered = businesses.filter(b => b.name.toLowerCase().includes(search.toLowerCase()));

    const statusColors = {
        active: 'bg-emerald-500/20 text-emerald-400',
        trial: 'bg-blue-500/20 text-blue-400',
        pending: 'bg-amber-500/20 text-amber-400',
        cancelled: 'bg-rose-500/20 text-rose-400',
        expired: 'bg-slate-500/20 text-slate-400',
    };

    return (
        <Layout>
            <div data-testid="admin-businesses-page">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-100">Businesses</h1>
                        <p className="text-slate-400 text-sm mt-1">{businesses.length} registered businesses</p>
                    </div>
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search businesses..."
                            className="pl-9 bg-slate-800 border-slate-700 text-slate-100" data-testid="search-businesses-input" />
                    </div>
                </div>

                {loading ? (
                    <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="h-20 bg-slate-900/60 rounded-xl animate-pulse" />)}</div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-16 text-slate-500"><Building2 className="h-12 w-12 mx-auto mb-3 opacity-30" /><p>No businesses found</p></div>
                ) : (
                    <div className="space-y-3">
                        {filtered.map((b) => (
                            <div key={b.id} className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`business-card-${b.id}`}>
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-base font-semibold text-slate-200 truncate">{b.name}</h3>
                                    <p className="text-xs text-slate-500 mt-0.5">{b.email || 'No email'}</p>
                                </div>
                                <div className="flex items-center gap-3 shrink-0">
                                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${statusColors[b.subscription_status] || statusColors.pending}`}>
                                        {b.subscription_status}
                                    </span>
                                    <span className="text-sm text-slate-400 flex items-center gap-1">
                                        <CreditCard className="h-3.5 w-3.5" /> £{b.subscription_price?.toFixed(2)}
                                    </span>
                                    <Button size="sm" variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-slate-200"
                                        onClick={() => handleViewBusiness(b.id)} data-testid={`view-business-${b.id}`}>
                                        <Eye className="h-3.5 w-3.5 mr-1" /> View
                                    </Button>
                                    <Button size="sm" variant="ghost"
                                        className={b.subscription_status === 'active' ? 'text-rose-400 hover:bg-rose-500/10 text-xs h-8' : 'text-emerald-400 hover:bg-emerald-500/10 text-xs h-8'}
                                        onClick={() => handleToggleStatus(b.id, b.subscription_status)} data-testid={`toggle-subscription-${b.id}`}>
                                        <Power className="h-3.5 w-3.5 mr-1" /> {b.subscription_status === 'active' ? 'Cancel' : 'Activate'}
                                    </Button>
                                    {(b.subscription_status === 'cancelled' || b.subscription_status === 'pending') && (
                                        <Button size="sm" variant={deletingId === b.id ? 'destructive' : 'ghost'}
                                            className={deletingId === b.id ? 'bg-rose-600 hover:bg-rose-700 text-white text-xs h-8' : 'text-rose-400 hover:bg-rose-500/10 text-xs h-8'}
                                            onClick={(e) => { e.stopPropagation(); handleDelete(b.id, b.name); }} data-testid={`delete-business-${b.id}`}>
                                            <Trash2 className="h-3.5 w-3.5 mr-1" /> {deletingId === b.id ? 'Confirm Delete' : 'Delete'}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Layout>
    );
}
